import React, { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Card, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Grid } from "@mui/material";
import { AttachMoney } from "@mui/icons-material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import Dropdown from "components/CustomDropdown/Dropdown";
import api from "utils/api";
import { toast } from "react-toastify";

const rupees = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const date = value => value ? new Date(value).toLocaleDateString("en-IN") : "Not scheduled";
const requestKey = () => globalThis.crypto?.randomUUID?.() || `fee-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

function FeeField({ label, children }) {
  return <>
    <ArgonTypography variant="button" fontWeight="regular" sx={{ display: "block", mb: 0.5 }}>
      {label}
    </ArgonTypography>
    {children}
  </>;
}
FeeField.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

export default function AdditionalFees({ classSessionId, tutorName = "", onSaved }) {
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState(null);
  const [form, setForm] = useState({ additionalAmount: "", newPerClassSalary: "", paymentDate: today(), applyFrom: "CURRENT", recurring: true, reason: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [requestId, setRequestId] = useState("");
  const endpoint = `/finance/classes/${classSessionId}/fee-revisions`;
  const update = (field, value) => { setForm(previous => ({ ...previous, [field]: value })); setPreview(null); setRequestId(requestKey()); };
  const load = async () => {
    setOpen(true); setBusy(true); setError(""); setPreview(null); setContext(null); setRequestId(requestKey());
    try {
      const { data } = await api.get(endpoint);
      setContext(data.data);
      setForm({ additionalAmount: "", newPerClassSalary: String(Math.round(data.data.currentHourlyRate * data.data.duration / 60 * 100) / 100),
        paymentDate: today(), applyFrom: data.data.remainingClasses > 0 ? "CURRENT" : "NEXT", recurring: true, reason: "" });
    } catch (e) { setError(e.response?.data?.message || "Could not load tuition details."); }
    finally { setBusy(false); }
  };
  const payload = () => ({ ...form, billingMode: "TOTAL", additionalAmount: Number(form.additionalAmount), newPerClassSalary: Number(form.newPerClassSalary) });
  const review = async () => {
    if (form.additionalAmount === "" || form.newPerClassSalary === "" || !form.paymentDate || !form.reason.trim()) {
      setError("Enter the additional amount, tutor salary per class, payment date and reason."); return;
    }
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`${endpoint}/preview`, payload());
      if (data.data?.billingMode !== "TOTAL" || !Array.isArray(data.data?.cycleAllocations)) {
        setError("The total fee split is unavailable. Please reload the page and try again.");
        return;
      }
      setPreview(data.data);
    }
    catch (e) { setError(e.response?.data?.message || "Could not calculate the preview."); }
    finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true); setError("");
    try {
      await api.post(endpoint, { ...payload(), requestId, contextVersion: preview.contextVersion });
      toast.success("Additional fees and tutor rate revision saved");
      setOpen(false); onSaved?.();
    } catch (e) {
      setError(e.response?.data?.message || "Save was not confirmed. Retry with the same details to avoid duplication.");
      if (e.response?.status === 409) setPreview(null);
    } finally { setBusy(false); }
  };
  const currentAmount = Number(form.additionalAmount || 0);
  const perClassSalary = Number(form.newPerClassSalary || 0);
  const currentPerClassSalary = Math.round(Number(context?.currentHourlyRate || 0) * Number(context?.duration || 60) / 60 * 100) / 100;
  const effectiveIndex = Number(context?.cycleIndex || 0) + (form.applyFrom === "NEXT" ? 1 : 0);
  const applicableCycles = (context?.cycles || []).filter(cycle =>
    cycle.cycleIndex >= effectiveIndex && cycle.remainingClasses > 0 &&
    (form.recurring || cycle.cycleIndex === effectiveIndex));
  const totalPaise = Number.isFinite(currentAmount) ? Math.max(0, Math.round(currentAmount * 100)) : 0;
  const eachPaise = applicableCycles.length ? Math.floor(totalPaise / applicableCycles.length) : 0;
  const leftoverPaise = applicableCycles.length ? totalPaise % applicableCycles.length : 0;
  const allocations = preview?.cycleAllocations || applicableCycles.map((cycle, index) => {
    const amount = (eachPaise + (index < leftoverPaise ? 1 : 0)) / 100;
    return { ...cycle, amount, revisedParentFee: Math.round((Number(cycle.currentParentFee) + amount) * 100) / 100 };
  });
  const selectedCycleAllocation = allocations.find(cycle => cycle.cycleIndex === effectiveIndex);
  const selectedTutorName = context?.tutorName || tutorName || "Tutor";
  const panelStyle = { borderRadius: 2, p: 1.5, backgroundColor: "#f8fafb", border: "1px solid #d9e2ec" };
  return <>
    <Card sx={{ p: 2, borderRadius: 3, minHeight: 120,
      background: "linear-gradient(135deg, #e8f5e8, #c8e6c9)", border: "1px solid #81c784" }}>
      <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
        <AttachMoney sx={{ color: "#388e3c", fontSize: 20 }} />
        <ArgonTypography variant="subtitle2" fontWeight="medium">Additional Fees</ArgonTypography>
      </ArgonBox>
      <ArgonTypography variant="body2" fontWeight="medium">{tutorName || "Tutor Fees"}</ArgonTypography>
      <ArgonButton variant="outlined" color="success" size="small" sx={{ mt: 1.25 }} onClick={load}>
        Additional Fees
      </ArgonButton>
    </Card>
    <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <ArgonBox display="flex" alignItems="center" gap={1}>
          <AttachMoney sx={{ color: "#2e7d32" }} />
          <ArgonTypography variant="h6" fontWeight="bold">Additional Fees</ArgonTypography>
        </ArgonBox>
      </DialogTitle>
      <DialogContent>
        <ArgonBox pt={1}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <FeeField label="Tutor Name">
                <Dropdown ariaLabel="Selected tutor" value={selectedTutorName}
                  options={[{ label: selectedTutorName, value: selectedTutorName }]}
                  width="100%" size="medium" disabled />
              </FeeField>
            </Grid>
            {busy && !context && <Grid item xs={12}>
              <ArgonBox display="flex" alignItems="center" gap={1} py={1}>
                <CircularProgress size={18} />
                <ArgonTypography variant="caption" color="text">Loading cycle details...</ArgonTypography>
              </ArgonBox>
            </Grid>}
            {context && <>
              <Grid item xs={12} sm={6}>
                <FeeField label="Parent Name">
                  <ArgonInput value={context.parentName || ""} inputProps={{ readOnly: true, "aria-label": "Parent Name" }} fullWidth sx={{ backgroundColor: "#e9ecef" }} />
                </FeeField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FeeField label="Student Name">
                  <ArgonInput value={context.studentNames || ""} inputProps={{ readOnly: true, "aria-label": "Student Name" }} fullWidth sx={{ backgroundColor: "#e9ecef" }} />
                </FeeField>
              </Grid>
              <Grid item xs={12}>
                <ArgonBox sx={panelStyle}>
                  <ArgonTypography variant="button" fontWeight="medium" display="block">{context.cycleName}</ArgonTypography>
                  <ArgonTypography variant="caption" color="text" display="block" mb={1}>
                    {date(context.cycleStartDate)} – {date(context.cycleEndDate)}
                  </ArgonTypography>
                  <Grid container spacing={1.25}>
                    {[["Total Classes", context.totalClasses], ["Completed", context.completedClasses],
                      ["Remaining", context.remainingClasses], ["Remaining Hours", context.remainingHours],
                      ["Current Tutor Salary", `${rupees(currentPerClassSalary)}/class`],
                      ["Parent Cycle Fee", rupees(context.currentParentFee)]].map(([label, value]) =>
                      <Grid item xs={6} sm={4} key={label}>
                        <ArgonTypography variant="caption" color="text" display="block">{label}</ArgonTypography>
                        <ArgonTypography variant="button" fontWeight="medium">{value}</ArgonTypography>
                      </Grid>
                    )}
                  </Grid>
                </ArgonBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FeeField label="Total Additional Fees (Parent)">
                  <ArgonInput fullWidth type="number" value={form.additionalAmount} disabled={busy}
                    placeholder="Enter total additional amount"
                    inputProps={{ min: 0, step: "0.01", "aria-label": "Total additional parent fee (₹)" }}
                    onChange={e => update("additionalAmount", e.target.value)} />
                </FeeField>
                <ArgonTypography variant="caption" color="text" display="block" mt={0.5}>
                  This total is divided across the applicable cycles.
                </ArgonTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FeeField label="Tutor Salary (Per Class)">
                  <ArgonInput fullWidth type="number" value={form.newPerClassSalary} disabled={busy}
                    placeholder="Enter tutor salary per class"
                    inputProps={{ min: 0.01, step: "0.01", "aria-label": "New tutor salary (₹/class)" }}
                    onChange={e => update("newPerClassSalary", e.target.value)} />
                </FeeField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FeeField label="Payment Date">
                  <ArgonInput fullWidth type="date" value={form.paymentDate} disabled={busy}
                    inputProps={{ "aria-label": "Payment date" }}
                    onChange={e => update("paymentDate", e.target.value)} />
                </FeeField>
              </Grid>
              <Grid item xs={12}>
                <FeeField label="Apply From">
                  <Dropdown ariaLabel="Apply from" width="100%" size="medium" value={form.applyFrom} disabled={busy}
                    onChange={e => update("applyFrom", e.target.value)}
                    options={[
                      { label: "Current cycle - remaining classes", value: "CURRENT", disabled: !context.remainingClasses },
                      { label: "Next cycle", value: "NEXT", disabled: !context.cycles?.some(cycle => cycle.cycleIndex === context.cycleIndex + 1 && cycle.remainingClasses > 0) },
                    ]} />
                </FeeField>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel sx={{ ml: 0, mr: 0 }} control={<Checkbox checked={form.recurring} disabled={busy}
                  onChange={e => update("recurring", e.target.checked)} />}
                  label={<ArgonTypography variant="button" fontWeight="regular">Split total across all remaining cycles</ArgonTypography>} />
                <ArgonTypography variant="caption" color="text" display="block">
                  {form.recurring ? "The tutor's revised salary per class also continues in future cycles." : "The full amount and tutor rate change apply only to the selected cycle."}
                </ArgonTypography>
              </Grid>
              <Grid item xs={12}>
                <FeeField label="Reason for Revision">
                  <ArgonInput fullWidth multiline rows={2} value={form.reason} disabled={busy}
                    placeholder="Enter reason for additional fees"
                    inputProps={{ maxLength: 500, "aria-label": "Reason for revision" }}
                    onChange={e => update("reason", e.target.value)} />
                </FeeField>
              </Grid>
              <Grid item xs={12}>
                <ArgonBox sx={panelStyle}>
                  <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                    {preview ? `Review - Cycle ${preview.effectiveCycleIndex + 1}` : "Fee Calculation"}
                  </ArgonTypography>
                  <Grid container spacing={1.25}>
                    <Grid item xs={12} sm={6}>
                      <ArgonBox sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: "#eef4ff", border: "1px solid #c7d7fe", height: "100%" }}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="info" display="block" mb={0.5}>Parent Fees</ArgonTypography>
                        <ArgonTypography variant="caption" display="block">Current Cycle Fee: {rupees(context.currentParentFee)}</ArgonTypography>
                        <ArgonTypography variant="caption" display="block">Total Additional Fee: {rupees(currentAmount)}</ArgonTypography>
                        <ArgonTypography variant="caption" display="block">Split Across: {allocations.length} cycle(s)</ArgonTypography>
                        {selectedCycleAllocation && <ArgonTypography variant="button" fontWeight="medium" display="block" mt={0.5}>
                          {selectedCycleAllocation.cycleName}: {rupees(selectedCycleAllocation.revisedParentFee)}
                        </ArgonTypography>}
                      </ArgonBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ArgonBox sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: "#edf7ed", border: "1px solid #c8e6c9", height: "100%" }}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="success" display="block" mb={0.5}>Tutor Pay</ArgonTypography>
                        <ArgonTypography variant="caption" display="block">Salary per Class: {rupees(perClassSalary)}</ArgonTypography>
                        <ArgonTypography variant="caption" display="block">Session Duration: {context.duration} min</ArgonTypography>
                        {(preview || form.applyFrom === "CURRENT") && <>
                          <ArgonTypography variant="caption" display="block">Extra Payout: {rupees(preview ? preview.extraTutorPay : (perClassSalary - currentPerClassSalary) * context.remainingClasses)}</ArgonTypography>
                          <ArgonTypography variant="button" fontWeight="medium" display="block" mt={0.5}>
                            {preview ? `Cycle Pay: ${rupees(preview.estimatedTutorPay)}` : `Remaining Pay: ${rupees(perClassSalary * context.remainingClasses)}`}
                          </ArgonTypography>
                        </>}
                      </ArgonBox>
                    </Grid>
                  </Grid>
                  <ArgonBox mt={1.5}>
                    <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>Cycle-wise Fee Split</ArgonTypography>
                    {allocations.length ? <>
                      <ArgonBox display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={1} mb={0.75}>
                        {["Cycle", "Additional Fee", "Revised Fee"].map(label =>
                          <ArgonTypography key={label} variant="caption" fontWeight="bold">{label}</ArgonTypography>)}
                      </ArgonBox>
                      {allocations.map(cycle => <ArgonBox key={cycle.cycleIndex} display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={1} py={0.5}>
                        <ArgonTypography variant="caption">{cycle.cycleName}</ArgonTypography>
                        <ArgonTypography variant="caption" fontWeight="medium">{rupees(cycle.amount)}</ArgonTypography>
                        <ArgonTypography variant="caption">{rupees(cycle.revisedParentFee)}</ArgonTypography>
                      </ArgonBox>)}
                      <ArgonBox display="flex" justifyContent="space-between" mt={1}>
                        <ArgonTypography variant="caption" fontWeight="bold">Total Additional Fee</ArgonTypography>
                        <ArgonTypography variant="caption" fontWeight="bold">{rupees(currentAmount)}</ArgonTypography>
                      </ArgonBox>
                    </> : <ArgonTypography variant="caption" color="text">No applicable cycles. Add renewal cycles before entering additional fees.</ArgonTypography>}
                  </ArgonBox>
                  {preview ? <ArgonBox mt={1.5}>
                    {[
                      ["Applicable remaining hours", preview.applicableHours],
                      ["Next cycle parent fee", rupees(preview.nextParentFee)],
                      ["Next cycle tutor pay (estimate)", rupees(preview.nextTutorPay)],
                      [preview.parentPaymentStatus === "PAID" ? "Additional fees marked paid on save" : "Additional invoices due now", `${preview.invoicesCreated} invoice(s) · ${rupees(preview.amountPaidOnSave ?? preview.amountDueNow)}`],
                    ].map(([label, value]) => <ArgonBox key={label} display="flex" justifyContent="space-between" gap={1} mb={0.5}>
                      <ArgonTypography variant="caption" color="text">{label}</ArgonTypography>
                      <ArgonTypography variant="caption" fontWeight="medium">{value}</ArgonTypography>
                    </ArgonBox>)}
                    <ArgonTypography variant="caption" color="text" display="block" mt={1}>
                      One paid invoice and one transaction will be created for the total amount. The cycle split above is for credit allocation only. Later renewals do not repeat this charge. Tutor pay follows eligible attendance.
                    </ArgonTypography>
                  </ArgonBox> : <ArgonTypography variant="caption" color="text" display="block" mt={1}>
                    Review changes to confirm this total and its cycle-wise split. Tutor payout is an estimate.
                  </ArgonTypography>}
                </ArgonBox>
              </Grid>
              {!!context.history.length && <Grid item xs={12}>
                <ArgonTypography variant="button" fontWeight="medium" display="block" mb={0.5}>Revision History</ArgonTypography>
                {context.history.map(item => <ArgonBox key={item.id} sx={{ ...panelStyle, mb: 1 }}>
                  <ArgonTypography variant="caption" fontWeight="medium" display="block">
                    {item.billingMode === "TOTAL"
                      ? `Total ${rupees(item.additionalAmount)} across ${(item.cycleAllocations || []).length} cycle(s)`
                      : `Cycle ${item.effectiveCycleIndex + 1}${item.recurring ? " onwards" : " only"}: +${rupees(item.additionalAmount)} per cycle`}
                    {" · "}{rupees(item.oldHourlyRate * context.duration / 60)} → {rupees(item.newPerClassSalary ?? item.newHourlyRate * context.duration / 60)}/class
                  </ArgonTypography>
                  {item.billingMode === "TOTAL" && <ArgonTypography variant="caption" color="text" display="block">
                    {(item.cycleAllocations || []).map(cycle => `Cycle ${cycle.cycleIndex + 1}: ${rupees(cycle.amount)}`).join(" · ")}
                  </ArgonTypography>}
                  <ArgonTypography variant="caption" color="text">{date(item.effectiveAt)} · {item.reason}</ArgonTypography>
                </ArgonBox>)}
              </Grid>}
            </>}
          </Grid>
        </ArgonBox>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <ArgonButton variant="outlined" color="secondary" size="small" disabled={busy} onClick={() => setOpen(false)}>Cancel</ArgonButton>
        {!context && !busy && <ArgonButton color="primary" size="small" onClick={load}>Retry</ArgonButton>}
        {context && <ArgonButton variant="contained" color="primary" size="small" disabled={busy || !allocations.length} onClick={preview ? save : review}>
          {busy ? "Please wait..." : preview ? "Confirm & Save" : "Review Changes"}
        </ArgonButton>}
      </DialogActions>
    </Dialog>
  </>;
}
AdditionalFees.propTypes = { classSessionId: PropTypes.string.isRequired, tutorName: PropTypes.string, onSaved: PropTypes.func };
